/**
 * checkout — the checkout page's own section (05/10/2026).
 *
 * The checkout was painted entirely from code, and it took its colours from the THEME: the page
 * background from `--aqx-theme-bg`, the ink from `--aqx-theme-ink`. A seller whose theme is dark then had
 * a black checkout — or, worse, the page pinned itself white while the text stayed the theme's white, and
 * the buyer paid on a page she could not read. The founder's decision: the checkout is its OWN page in
 * the builder, with its own colours and sizes, independent of the theme by default and editable like any
 * other page.
 *
 * What a seller can change here is the LOOK: colours, sizes, logo, shapes, what the summary shows. What
 * she cannot change is the FORM — which fields the buyer fills, the payment methods, the money. Those are
 * the storefront's (and the server's) and no setting reaches them. Shopify draws exactly this line in its
 * own checkout editor, for the same reason: a checkout a seller can break is a checkout nobody pays on.
 *
 * **Every colour has an explicit default.** Unlike the content sections, nothing here falls back to the
 * theme scheme: white page, light-grey summary column, near-black ink, light borders. Only the ACCENT
 * (buttons, links, the selected option) starts on the theme's accent token, because a brand colour on
 * the pay button IS the brand. A seller who wants the checkout to match a dark theme picks the colours.
 *
 * **The pay-button label starts EMPTY.** Empty means "keep saying what the shop already says, in the
 * shop's own language" — the dictionary carries it in every storefront language. A seeded sentence would
 * freeze one language into every store's document (the `order-confirmation` lesson).
 */
export const checkoutSchema = {
    type: 'checkout',
    name: 'Checkout',
    version: 1,
    templates: ['checkout'],
    fields: [
        // ── Logo ──────────────────────────────────────────────────────────────────
        {
            id: 'logoPosition',
            type: 'select',
            label: 'Logo position',
            group: 'Logo',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
        },
        {
            id: 'logoWidth',
            type: 'range',
            label: 'Logo width',
            group: 'Logo',
            info: 'The logo is the one uploaded in your header. The shop name is shown when there is none.',
            default: 120,
            min: 40,
            max: 320,
            step: 4,
            unit: 'px',
        },
        {
            id: 'showCartLink',
            type: 'checkbox',
            label: 'Show the cart icon',
            group: 'Logo',
            default: true,
        },
        // ── Colors ────────────────────────────────────────────────────────────────
        {
            id: 'mainBackground',
            type: 'color',
            label: 'Page background',
            group: 'Colors',
            default: { ref: 'custom', value: '#ffffff' },
        },
        {
            id: 'summaryBackground',
            type: 'color',
            label: 'Order summary background',
            group: 'Colors',
            default: { ref: 'custom', value: '#f5f5f5' },
        },
        {
            id: 'textColor',
            type: 'color',
            label: 'Text',
            group: 'Colors',
            default: { ref: 'custom', value: '#1a1a1a' },
        },
        {
            id: 'borderColor',
            type: 'color',
            label: 'Borders and fields',
            group: 'Colors',
            default: { ref: 'custom', value: '#dedede' },
        },
        {
            id: 'accentColor',
            type: 'color',
            label: 'Buttons and links',
            group: 'Colors',
            info: 'The pay button, the links and the selected option.',
            default: { ref: 'theme', token: 'accent' },
        },
        {
            id: 'buttonTextColor',
            type: 'color',
            label: 'Button text',
            group: 'Colors',
            default: { ref: 'custom', value: '#ffffff' },
        },
        // ── Typography ────────────────────────────────────────────────────────────
        {
            id: 'bodyFontSize',
            type: 'range',
            label: 'Text size',
            group: 'Typography',
            default: 14,
            min: 12,
            max: 20,
            step: 1,
            unit: 'px',
        },
        {
            id: 'headingFontSize',
            type: 'range',
            label: 'Heading size',
            group: 'Typography',
            default: 20,
            min: 14,
            max: 36,
            step: 1,
            unit: 'px',
        },
        {
            id: 'buttonFontSize',
            type: 'range',
            label: 'Button text size',
            group: 'Typography',
            default: 16,
            min: 12,
            max: 24,
            step: 1,
            unit: 'px',
        },
        // ── Button ────────────────────────────────────────────────────────────────
        {
            id: 'buttonShape',
            type: 'select',
            label: 'Button corners',
            group: 'Button',
            default: 'theme',
            options: [
                { value: 'theme', label: 'As in the theme' },
                { value: 'square', label: 'Square' },
                { value: 'rounded', label: 'Rounded' },
                { value: 'pill', label: 'Pill' },
            ],
        },
        {
            id: 'fieldShape',
            type: 'select',
            label: 'Field corners',
            group: 'Button',
            default: 'theme',
            options: [
                { value: 'theme', label: 'As in the theme' },
                { value: 'square', label: 'Square' },
                { value: 'rounded', label: 'Rounded' },
                { value: 'pill', label: 'Pill' },
            ],
        },
        {
            id: 'payButtonLabel',
            type: 'text',
            label: 'Pay button label',
            group: 'Button',
            info: 'Leave empty to keep your shop’s own wording, in your shop’s language.',
            default: '',
        },
        // ── Summary ───────────────────────────────────────────────────────────────
        {
            id: 'showThumbnails',
            type: 'checkbox',
            label: 'Show product images',
            group: 'Summary',
            default: true,
        },
        {
            id: 'showPromoField',
            type: 'checkbox',
            label: 'Show the discount code field',
            group: 'Summary',
            default: true,
        },
        {
            id: 'showSavings',
            type: 'checkbox',
            label: 'Show the total savings',
            group: 'Summary',
            default: true,
        },
        // ── Footer ────────────────────────────────────────────────────────────────
        {
            id: 'showSecureNote',
            type: 'checkbox',
            label: 'Show the "secure and encrypted" note',
            group: 'Footer',
            default: true,
        },
        {
            id: 'showPaymentBadges',
            type: 'checkbox',
            label: 'Show the payment badges',
            group: 'Footer',
            info: 'Visa, Mastercard, Amex, PayPal, Apple Pay, Google Pay — under the pay button.',
            default: false,
        },
    ],
};
/** The settings a brand-new checkout page carries — the SAME values the storefront used to hard-code. */
export const checkoutPresets = [
    {
        name: 'Checkout',
        settings: {
            logoPosition: 'center',
            logoWidth: 120,
            showCartLink: true,
            mainBackground: { ref: 'custom', value: '#ffffff' },
            summaryBackground: { ref: 'custom', value: '#f5f5f5' },
            textColor: { ref: 'custom', value: '#1a1a1a' },
            borderColor: { ref: 'custom', value: '#dedede' },
            accentColor: { ref: 'theme', token: 'accent' },
            buttonTextColor: { ref: 'custom', value: '#ffffff' },
            bodyFontSize: 14,
            headingFontSize: 20,
            buttonFontSize: 16,
            buttonShape: 'theme',
            fieldShape: 'theme',
            payButtonLabel: '',
            showThumbnails: true,
            showPromoField: true,
            showSavings: true,
            showSecureNote: true,
            showPaymentBadges: false,
        },
        blocks: [],
    },
];
/**
 * Not offered in "Add section": the checkout page carries exactly one of these, and a page with two
 * checkouts would ask the buyer to pay twice. The group is set anyway so that if the picker ever lists
 * it, it lands next to the cart.
 */
export const checkoutMeta = {
    label: 'Checkout',
    group: 'Cart',
    icon: 'credit-card',
};
export const checkoutDefinition = {
    type: 'checkout',
    schema: checkoutSchema,
    presets: checkoutPresets,
    meta: checkoutMeta,
};
//# sourceMappingURL=schema.js.map